import { useIntl } from 'react-intl'

import type { PassiveTagCellProps } from './PassiveTagCell.types'
import { TagBadge } from './TagBadge'
import { TagOverflow } from './TagOverflow'
import { useVisibleTagCount } from './useVisibleTagCount'

/**
 * Read-only twin of `TagCell` for the GraphQL lists: same badges, inherited
 * tooltips, 2-row overflow counting and empty state, but no editing, selection
 * or `TaggingProvider` dependency.
 */
export const PassiveTagCell = (props: PassiveTagCellProps) => {
  const { tags } = props
  const intl = useIntl()
  const hasTags = tags.length > 0

  // Rows can be replaced by a refetch that keeps the tag count but changes the
  // labels, so the measurement is keyed by the ordered tag content as well.
  const tagContentKey = tags.map(tag => `${tag.id}:${tag.name}`).join('|')

  const { containerRef, visibleCount, isMeasuring } = useVisibleTagCount(
    hasTags ? tags.length : 0,
    tagContentKey,
  )

  if (!hasTags) {
    return (
      <div className="TagCell">
        <span className="TagCell-empty">
          {intl.formatMessage({ id: 'Tags_no_tags' })}
        </span>
      </div>
    )
  }

  return (
    <div className="TagCell">
      <div ref={containerRef} className="TagCell-tags">
        {(isMeasuring ? tags : tags.slice(0, visibleCount)).map(tag => (
          <TagBadge
            key={tag.id}
            color={tag.color}
            inherited={tag.inherited}
            inheritedFrom={tag.inheritedFrom}
            name={tag.name}
            onRemove={() => undefined}
            showRemove={false}
          />
        ))}

        <TagOverflow count={isMeasuring ? 0 : tags.length - visibleCount} />
      </div>
    </div>
  )
}
